import React, { Fragment, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet-async';
import omit from 'lodash/omit';
import { useSelector } from 'redux/hooks';
import { getPageLoading } from 'redux/modules/page/selectors';

import urls from 'constants/urls';
import { AemComponentEntity, ProductEntity } from 'constants/data-shapes/product-entity';
import { sortByOptions } from 'constants/sortByOptions';
import { useOnCancelAmends } from 'hooks/use-on-cancel-amends';

import { colourScallopGrey } from '@johnlewispartnership/wtr-ingredients/foundations/colours';
import { concatenateTitle } from 'utils/concatenate-title-object';

import EmptyOffer from 'components/ProductGrid/Empty/Offer';
import OffersHeader from 'components/Offers/Header';
import ProductGrid from 'components/ProductGrid';
import ProductsRefinements from 'components/Product/Refinements';

import Canonical from 'components/Canonical';
import BrowseHeader from 'components/BrowseHeader/BrowseHeader';
import ContentLocation from 'components/ContentLocation';
import Skeleton from 'components/Skeleton';
import NotFound from 'components/NotFound/NotFound';

import { OffersHotspots } from 'components/adtech/OffersHotspots';
import { OffersLeaderboards } from 'components/adtech/OffersLeaderboard';

import styles from './OffersPage.scss';

const offersPageDefaultPropLocation = {};
const offersPageDefaultPropNavigation = [];
const offersPageDefaultPropProducts = [];
const offersPageDefaultPropSubCategories = [];
const offersPageDefaultPropTitle = { display: false };

const OffersPage = ({
  canonicalHref = '',
  locationsLoading,
  productsLoading,
  location = offersPageDefaultPropLocation,
  metaDescription = '',
  metaKeywords = '',
  metaTitle = '',
  navigation = offersPageDefaultPropNavigation,
  products = offersPageDefaultPropProducts,
  subCategories = offersPageDefaultPropSubCategories,
  title = offersPageDefaultPropTitle,
  fetchOfferProducts,
  pageNotFound = false,
}) => {
  useOnCancelAmends(fetchOfferProducts);

  const isPageLoading = useSelector(getPageLoading);
  const pageLoading = isPageLoading || locationsLoading;

  const concatenatedTitle = concatenateTitle(title);
  const hasNavigation = navigation && navigation.length > 0;

  const headerProps = {
    hideBreadcrumbs: true,
    hideContent: true,
    linksLabel: hasNavigation
      ? {
          display: true,
          highlight: 'offers',
          suffix: 'by Category',
          text: 'Browse',
        }
      : null,
    location,
    products,
    subCategories,
    title: {
      ...omit(title, 'display'),
      display: !hasNavigation && title.display,
    },
  };

  const metaProps = {
    title: metaTitle || concatenatedTitle,
    meta: [
      { name: 'description', content: metaDescription },
      { name: 'keywords', content: metaKeywords },
    ],
  };

  const listDefinitions = useMemo(
    () => ({
      curation: 'Promotions',
      format: 'List',
      page: 'Offers',
      title: title.text,
    }),
    [title],
  );

  if (pageNotFound) return <NotFound />;

  return (
    <Fragment>
      <Helmet {...metaProps} />
      <Canonical absolute href={canonicalHref} />
      {pageLoading && <Skeleton height="100vh" withSpinner spinnerPosition="top" />}
      {!pageLoading && (
        <Fragment>
          <OffersHeader />
          <div className={styles.contentBackgroundColour}>
            <ContentLocation name="masthead" preloadImages />
          </div>
          <div className={styles.offersCategories}>
            <BrowseHeader {...headerProps} />
            <ContentLocation name="header" />
            {location.pathname === urls.offers && (
              <>
                <OffersHotspots />
                <OffersLeaderboards />
              </>
            )}
          </div>
          <ProductsRefinements hideCategoryFilter sortOptions={sortByOptions.offers} />
          {productsLoading ? (
            <Skeleton
              withSpinner
              spinnerPosition="top"
              height="100vh"
              backgroundColor={colourScallopGrey}
              data-testid="products-loading"
            />
          ) : (
            <ProductGrid Empty={EmptyOffer} listDefinitions={listDefinitions} />
          )}
        </Fragment>
      )}
    </Fragment>
  );
};

OffersPage.displayName = 'OffersPage';

OffersPage.propTypes = {
  canonicalHref: PropTypes.string,
  locationsLoading: PropTypes.bool.isRequired,
  productsLoading: PropTypes.bool.isRequired,
  location: PropTypes.shape({
    key: PropTypes.string,
    pathname: PropTypes.string,
    search: PropTypes.string,
  }),
  metaDescription: PropTypes.string,
  metaKeywords: PropTypes.string,
  metaTitle: PropTypes.string,
  navigation: PropTypes.arrayOf(PropTypes.shape({})),
  products: PropTypes.arrayOf(PropTypes.oneOfType([ProductEntity(), AemComponentEntity()])),
  subCategories: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      type: PropTypes.string,
      url: PropTypes.string,
    }),
  ),
  title: PropTypes.shape({
    display: PropTypes.bool,
  }),
  fetchOfferProducts: PropTypes.func,
  pageNotFound: PropTypes.bool,
};

export default OffersPage;
